// ============================================================
// MOONSHINE MASTERS — World3D.js        THE WOODS (placeholder scaffold)
//
// Everything here is PLACEHOLDER GEOMETRY on purpose (caP'n: ship the
// scaffold first so the seams can be proven; the art replaces it later).
// What is NOT placeholder is the seams:
//   * logic -> data <- renderer: this file reads moonshineData.js and
//     the reducer's state; it never decides a rule.
//   * PERF LAW: nothing here puts React state in a frame. The Controller
//     only ever ANNOUNCES a changed prompt / place; moving, the beasts,
//     the sky and the foraging orbs all live in refs and one useFrame
//     per SYSTEM.
//   * THE CAMERA CONTRACT: ieCam.js (Follow/Trail, free pose, Default,
//     movement follows the eye), copied from Hooked.
//   * THE FAIL-SAFE LAW: <FrameWatch/> mounted once; Hero.js owns the swap.
//   * No Math.random in render: world gen is mulberry32, seeded.
//
// 2026-10-24 THE BLENDER PASS: once moonshine_kit.glb lands (Woods3D.jsx), the pines, the yard, the ruins and
// every gather spot are the real models; the primitives below stay as the FAIL-SAFE until then (and forever if the file is missing).
// BORROW SEAMS (PRIME §3), each marked where it will land:
//   [BORROW] player      -> Hero.js (done: Terror's Lyraei / Laostre, byte-identical)
//   [BORROW] forest      -> Annihilation grove_kit.glb       (trees below are boxes/cones)
//   [BORROW] shack/shop  -> Hooked shop_hooked.glb           (shack below is a box)
//   [BORROW] beasts      -> ieBeingModels Spectral Tiger/Shadow Fox (PhantomBeast below)
//   [NEW]    still, barrels, jars, mash tub -> Blender       (primitives below)
// ============================================================
import React, { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import * as THREE from "three";
import * as CAM from "./ieCam";
import { FrameWatch } from "./sculptGate";
import { Hero } from "./Hero";
import { ToonzPerson } from "./moonshineFolk3D";
import { typing } from "./moonshineOnline";
import { useBrewerSync, useRemoteBrewers, RemoteBrewers } from "./moonshineBrewers";
import { claimPick, claimWisp, useSharedWisp, useSharedPicks, sendBeasts, useBeastFeed, sendCatch, useCaught, BEAST_SEND_CHASE_MS, BEAST_SEND_IDLE_MS, BEAST_STALE_MS } from "./moonshineWoods";
import { WORLD, LAYOUT, PLACES, INGREDIENTS, TUNING, WALK, BEAST, REACH, STATIONS, RECIPES, RIG_SPOTS, HEAT, BLOOD, TROPHY, AGE, YARD_UP, DECOR, beastSpeed, zoneOf, charmK } from "./moonshineData";
import { GEN, slide, mulberry32, wispAt, setCampLevel, campKey } from "./worldgen";
import { say as words } from "./text";
import { offeredList, acceptedList, personOf } from "./missions";
import { dayPhase, isNight, tubPhase, barrelPhase, readyBarrel, mashTemp, pitchZone, fuzzIn, clearMs, isBloodMoon, stillOf, LABEL_W, LABEL_H } from "./moonshine";
import { drawLabel } from "./theme";
import { useKit, Woods, GatherSpots, StillRig, hasRigKit } from "./Woods3D";
import { stepAt, targetOf } from "./tutorial";   // 2026-10-24: the Blender woods (falls back to the placeholders below)

// session-only runtime (module-level on purpose: React context doesn't cross <Canvas>)
export const WILD = { pickedAt: {}, invulnUntil: 0, snapCam: false, fx: { still: "idle", still2: "idle" }, sid: null, charm: null, eyes: false, out: false, wispN: null,
  clear: { since: 0, need: 0 } };   // clear (STARS, 2026-10-31): since = ms the whole crew has been out past the lanterns in one go (0 = not all out); need = clearMs   // out: past the lanterns right now (the Eyes can't come off out there)   // sid: the server whose woods we share (null = solo)

// ── THE CONTROLLER - one frame loop, returns null ────────────
function Controller({ posRef, yawRef, speedRef, keysRef, actRef, frozen, bagRef, spookRef, gameRef, cb }) {
  const vel = useRef(0);
  const promptId = useRef("");
  const placeId = useRef("");
  const cur = useRef(null);
  const lastBank = useRef(0);
  const walked = useRef(0);                              // the tutorial's "walk around": 4 m, announced once
  const wasFrozen = useRef(false);                       // a panel just closed: the key that closed it must not reopen it (critic 2026-10-23)
  const seenYardR = useRef(WORLD.YARD_R);                 // the lantern line I last stood inside (a camp change keeps me inside it)
  const seenCamp = useRef(campKey());                    // THE LIVE CAMP: a new camp / a bigger shack / still 2 can put a collider where I stand

  useFrame((_, dtRaw) => {
    const dt = Math.min(dtRaw, 0.05);
    const k = keysRef.current;
    let ix = 0, iy = 0;
    if (!frozen) {
      if (k["ArrowUp"] || k["w"] || k["W"]) iy -= 1;
      if (k["ArrowDown"] || k["s"] || k["S"]) iy += 1;
      if (k["ArrowLeft"] || k["a"] || k["A"]) ix -= 1;
      if (k["ArrowRight"] || k["d"] || k["D"]) ix += 1;
    }
    const mag = Math.hypot(ix, iy);
    if (mag > 1) { ix /= mag; iy /= mag; }
    const r = CAM.rotateInput(ix, iy);                 // MOVEMENT FOLLOWS THE EYE
    const running = !!(k["Shift"] || k["shift"]);
    const want = Math.hypot(r.dx, r.dy) > 0.001 ? WALK.SPEED * (running ? WALK.RUN : 1) : 0;
    vel.current += (want - vel.current) * Math.min(1, dt * WALK.ACCEL);
    if (vel.current < 0.02) vel.current = 0;

    const p = posRef.current;
    if (seenCamp.current !== campKey()) {                 // ...so step out of it once (otherwise slide() would pin me there for good)
      seenCamp.current = campKey();
      // ...and if I was inside the lanterns, I stay inside them (a camp that SHRANK under me - a friend's smaller yard - must not drop me in the woods)
      const oldR = seenYardR.current || WORLD.YARD_R, rr0 = Math.hypot(p.x, p.z);
      if (rr0 < oldR && rr0 > WORLD.YARD_R - 1.2) { const k = (WORLD.YARD_R - 1.2) / rr0; p.x *= k; p.z *= k; }
      seenYardR.current = WORLD.YARD_R;
      for (let n = 0; n < 4; n++) {
        const o = GEN.solids.find((c) => Math.hypot(p.x - c.x, p.z - c.z) < c.r + 0.3); if (!o) break;
        const dx = p.x - o.x || 0.001, dz = p.z - o.z, d = Math.hypot(dx, dz), k = (o.r + 0.32) / d;
        p.x = o.x + dx * k; p.z = o.z + dz * k;
      }
    }
    if (want > 0) {
      const len = Math.hypot(r.dx, r.dy) || 1, step = vel.current * dt;
      const [nx, nz] = slide(p.x, p.z, (r.dx / len) * step, (r.dy / len) * step);
      if (walked.current < 4) { walked.current += Math.hypot(nx - p.x, nz - p.z); if (walked.current >= 4 && cb.onMoved) cb.onMoved(); }
      p.x = nx; p.z = nz;
      yawRef.current = Math.atan2(r.dx / len, r.dy / len);
    }
    speedRef.current = vel.current / WALK.SPEED;

    const now = Date.now();
    const spooked = now < spookRef.current;
    const wall = WORLD.YARD_R - 0.9, rr = Math.hypot(p.x, p.z);
    if (spooked && rr > wall) { p.x *= wall / rr; p.z *= wall / rr; }       // too spooked to cross the lantern line
    const inYard = Math.hypot(p.x, p.z) < WORLD.YARD_R;
    WILD.out = !inYard;
    // home safe: banking happens the moment you cross the lantern line
    if (inYard && bagRef.current > 0 && now - lastBank.current > 600) { lastBank.current = now; cb.onBank(); }

    // which place are you in? (announced on change only)
    let pid = "";
    for (const pl of PLACES) if ((!pl.eyes || WILD.eyes) && Math.hypot(p.x - pl.x, p.z - pl.z) < pl.r + 2.5) { pid = pl.id; break; }
    if (!pid && inYard) pid = "yard";
    if (pid !== placeId.current) { placeId.current = pid; cb.onPlace(pid); }

    // what's near
    let best = null, bd = 1e9;
    const consider = (d, c) => { if (d < bd) { bd = d; best = c; } };
    if (spooked && Math.hypot(p.x, p.z) > wall - 1.4) consider(0.01, { id: "spooked", verb: "Too spooked", label: "you'll go back out at dawn", color: "#8f8fb8", disabled: true });
    // THE RUN's stations (2026-10-24): each prompt says what you can do THERE, right now
    const g = gameRef.current;
    if (g) {
      const tp = tubPhase(g.tub, now);
      const dTub = Math.hypot(p.x - STATIONS.tub[0], p.z - STATIONS.tub[1]);
      if (dTub < REACH.tub) consider(dTub, tp === "empty" ? { id: "tub", verb: "Mash in", label: "the mash tub - pick a recipe", color: "#e2b93b" }
        : tp === "cooking" ? { id: "tub:cooking", verb: "The mash is cooking", label: "keep it stirred", color: "#e2b93b", panel: "tub" }
        : Number.isFinite(g.tub.stirAt) && ["hot", "warm"].includes(pitchZone(mashTemp(g.tub, now))) ? { id: "tub:cooling", verb: "The mash is cooling", label: "too hot for the yeast yet", color: "#e8a84a", panel: "tub" }
        : { id: "tub:cooked", verb: "The mash is ready", label: "pour it into a barrel", color: "#8fd88a", panel: "tub" });
      STATIONS.barrels.forEach(([bx, bz], i) => {
        const d = Math.hypot(p.x - bx, p.z - bz); if (d > REACH.barrel) return;
        const b = g.barrels[i], ph = barrelPhase(b, now);
        consider(d, ph === "empty" ? (tp === "cooked" ? (Number.isFinite(g.tub.stirAt) && ["hot", "warm"].includes(pitchZone(mashTemp(g.tub, now))) ? { id: "pour" + i + ":hot", verb: "Pour the mash in", label: "it's still hot - that'll hurt the yeast", color: "#e8a84a", pour: i }
                                                       : { id: "pour" + i, verb: "Pour the mash in", label: "start it fermenting", color: "#8fd88a", pour: i })
                                                       : { id: "barrel" + i + ":empty", verb: "Empty barrel", label: "mash in at the tub first", color: "#a8977a", disabled: true })
          : ph === "working" ? { id: "barrel" + i + ":working", verb: "Fermenting", label: RECIPES[b.recipe].name, color: "#e8a84a", panel: "barrel:" + i }
          : { id: "barrel" + i + ":ready", verb: "Wash is ready", label: "run it through the still", color: "#8fd88a", panel: "barrel:" + i });
      });
      // THE STILL(S): the same prompts for both; the second one (a yard upgrade, 2026-10-31) opens panel "still2" and says "Still 2"
      const stillPrompt = (k) => {
        const at = k ? LAYOUT.still2 : LAYOUT.still, d = Math.hypot(p.x - at[0], p.z - at[1]);
        if (d >= REACH.still) return;
        const st = stillOf(g, k), pre = k ? "still2" : "still", name = k ? "Still 2" : "The Still";
        consider(d, g.seized ? { id: pre + ":seized", verb: k ? "Still 2 is gone" : "The still is gone", label: "the revenuers took it", color: "#e8706a", panel: pre }
          : st.broken ? { id: pre + ":broken", verb: name + " is busted", label: "fix it", color: "#e8706a", panel: pre }
          : st.batch && st.batch.done ? { id: pre + ":blend", verb: k ? "Blend (Still 2)" : "Blend", label: "pick your jars", color: "#8fd88a", panel: pre }
          : st.batch ? { id: pre + ":run", verb: name, label: "swap jars - mind the fire", color: "#e8a84a", panel: pre }
          : k && ["head", "cond", "seal"].some((x) => !(g.rig2 || {})[x]) ? { id: pre + ":bare", verb: "Still 2 isn't built", label: "buy its parts on THE RIG", color: "#a8977a", panel: pre }
          : readyBarrel(g, now) >= 0 ? { id: pre + ":load", verb: k ? "Run Still 2" : "Run the still", label: "a barrel is ready", color: "#8fd88a", panel: pre }
          : { id: pre + ":idle", verb: name, label: "nothing to run yet", color: "#a8977a", panel: pre });
      };
      stillPrompt(0);
      if (g.stills === 2) stillPrompt(1);
    }
    const dBoard = Math.hypot(p.x - LAYOUT.board[0], p.z - LAYOUT.board[1]);
    if (dBoard < REACH.board) consider(dBoard, { id: "board", verb: "Tome Contracts", label: "who wants what", color: "#f2d98a" });
    // THE LOCKBOX (2026-10-31): the shack. Not during the tutorial (the tutorial's prompts stay exactly as they were).
    const dShack = Math.hypot(p.x - LAYOUT.shack[0], p.z - LAYOUT.shack[1]), shackS = (YARD_UP.shack[g ? g.shack : 0] || YARD_UP.shack[0]).s;   // a bigger shack reaches further
    if (g && g.tut && g.tut.done && dShack < REACH.shack + (shackS - 1) * 2.3) consider(dShack, { id: "lockbox", verb: "The Lockbox", label: "they can't touch what's in here", color: "#d9b26a", panel: "lockbox" });
    if (!inYard) {
      const night = isNight(now);
      for (const n of GEN.nodes) {
        const d = Math.hypot(p.x - n.x, p.z - n.z);
        if (d > REACH.node) continue;
        const at = WILD.pickedAt[n.id];
        if (at && now - at < TUNING.NODE_RESPAWN_S * 1000) continue;
        const ing = INGREDIENTS[n.ing];
        if (n.eyes && !WILD.eyes) continue;                                         // hidden: only the Eyes see it
        if (n.night && !night) consider(d, { id: "sleep_" + n.id, verb: "It's sleeping", label: "moonbloom only opens after dark", color: "#8f8fb8", disabled: true });
        else consider(d, { id: n.id, node: n, verb: "Gather " + ing.name, label: ing.rare ? "a far-patch find" : "", color: ing.color });
      }
      // THE UNSTABLE SIGHTS (2026-10-30): only through the Eyes - the Stray Wisp (one quick mission at a time) and the four sights
      if (WILD.eyes && g) {
        if (!(g.bag && g.bag.wisp > 0) && !(WILD.sid && WILD.wispN == null)) {     // on a server, no wisp until the crew's count has arrived
          const w = wispAt(liveWisp(g)), d = Math.hypot(p.x - w.x, p.z - w.z);
          if (d < REACH.node + 0.4) consider(d, { id: "wisp", wisp: true, verb: "Catch the Stray Wisp", label: "get it home past the lanterns", color: INGREDIENTS.wisp.color });
        }
        for (const sg of GEN.sights) {
          if (g.sights && g.sights[sg.id]) continue;
          const d = Math.hypot(p.x - sg.x, p.z - sg.z);
          if (d < REACH.node + 0.6) consider(d, { id: "sight:" + sg.id, sight: sg.id, verb: "Look closer", label: words(`sight.${sg.id}.name`).toLowerCase(), color: sg.color });
        }
      }
    }
    cur.current = best;
    const pid2 = best ? best.id : "";
    if (pid2 !== promptId.current) { promptId.current = pid2; cb.onPrompt(best ? { id: best.id, verb: best.verb, label: best.label, color: best.color, disabled: !!best.disabled } : null); }

    if (actRef.current) {
      actRef.current = false;
      const c = cur.current;
      if (c && !c.disabled && !frozen && !wasFrozen.current) {
        if (c.panel) cb.onPanel(c.panel);
        else if (c.id === "tub") cb.onPanel("tub");
        else if (c.pour != null) cb.onPour(c.pour);
        else if (c.id === "board") cb.onPanel("board");
        else if (c.wisp) {
          if (!WILD.sid) cb.onGather("wisp");                                               // solo: it's yours
          else { const n = WILD.wispN; WILD.wispN = n + 1;                                // shared: gone for me at once, the claim decides who has it
            claimWisp(WILD.sid, n, WILD.myName).then((r) => { if (r.won) cb.onGather("wisp"); else cb.onNote(`${r.by || "A friend"} got to the wisp first.`); }); }
        }
        else if (c.sight) { if (cb.onSight) cb.onSight(c.sight); }
        else if (c.node) {
          const node = c.node;
          WILD.pickedAt[node.id] = now;                                  // gone for ME at once (no double-press while the claim travels)
          if (!WILD.sid) cb.onGather(node.ing);                          // solo: exactly as before
          else claimPick(WILD.sid, node.id, TUNING.NODE_RESPAWN_S * 1000, WILD.myName).then((r) => {   // shared woods: only the winner gets it
            if (r.won) cb.onGather(node.ing);
            else cb.onNote(`${r.by || "A friend"} got there first.`);                                    // the loser hears it, in a few friendly words
          });
        }
      }
    }
    wasFrozen.current = frozen;
  });
  return null;
}

// ── THE CAMERA - the contract, all four pieces (from Hooked) ─
const PRESET = { back: 8.6, up: 4.3, look: 1.3 };
const CAM_EASE = { TURN: 2.2, POS: 3.4, LOOK: 7.7 };   // per second: how fast the cam swings behind you / follows / re-aims (POS 3.4 + LOOK 7.7 = the old 0.055 + 0.12 at 60 fps)
function CameraRig({ posRef, yawRef }) {
  const controls = useRef();
  const { camera } = useThree();
  const posed = useRef(false);
  const last = useRef(new THREE.Vector3());
  const seenReset = useRef(CAM.getResetCount());
  const seenPose = useRef(CAM.getPoseRelease());
  const goal = useMemo(() => new THREE.Vector3(), []);
  const tgt = useMemo(() => new THREE.Vector3(), []);
  const tmp = useMemo(() => new THREE.Vector3(), []);
  const first = useRef(true);
  // THE SPIRAL FIX (2026-10-30): the follow cam swings behind you only as much as you walk AWAY from it. Strafing (A/D) or
  // backing up (S) leaves it where it is, so holding A walks you across the screen instead of round and round. Every ease is
  // per SECOND, not per frame, so 30 fps and 144 fps feel the same. Dials: CAM_EASE.
  const camYaw = useRef(null);
  useEffect(() => () => CAM.resetForward(), []);
  useFrame((_, dtRaw) => {
    const dt = Math.min(dtRaw, 0.1);
    const c = controls.current, p = posRef.current;
    if (!c || !p) return;
    const mult = CAM.getMult();
    if (WILD.snapCam) { posed.current = false; last.current.copy(p); }       // teleported (caught): no swoop through the trees
    if (CAM.getResetCount() !== seenReset.current) { seenReset.current = CAM.getResetCount(); posed.current = false; camYaw.current = yawRef.current || 0; }   // DEFAULT = behind you NOW
    if (CAM.getPoseRelease() !== seenPose.current) { seenPose.current = CAM.getPoseRelease(); posed.current = false; camYaw.current = yawRef.current || 0; }
    tgt.set(p.x, p.y + PRESET.look, p.z);
    tmp.copy(p).sub(last.current); last.current.copy(p);
    if (posed.current) { camera.position.add(tmp); c.target.add(tmp); }   // FREE POSE: translate, never re-aim
    else {
      const back = PRESET.back * mult, up = PRESET.up * (0.55 + 0.45 * mult), py = yawRef.current || 0;
      if (camYaw.current === null || first.current || WILD.snapCam) camYaw.current = py;
      let dy = py - camYaw.current; dy = Math.atan2(Math.sin(dy), Math.cos(dy));          // -PI..PI
      const away = Math.max(0, Math.cos(dy)) * Math.min(1, tmp.length() / Math.max(dt, 1e-3) / 0.5);   // only while walking AWAY from the eye
      camYaw.current += dy * (1 - Math.exp(-CAM_EASE.TURN * away * dt));
      const y = camYaw.current;
      goal.set(p.x - Math.sin(y) * back, p.y + up, p.z - Math.cos(y) * back);
      if (first.current || WILD.snapCam) { camera.position.copy(goal); c.target.copy(tgt); first.current = false; WILD.snapCam = false; }
      camera.position.lerp(goal, 1 - Math.exp(-CAM_EASE.POS * dt)); c.target.lerp(tgt, 1 - Math.exp(-CAM_EASE.LOOK * dt));
    }
    if (camera.position.y < p.y + 0.6) camera.position.y = p.y + 0.6;
    c.update();
    tmp.copy(c.target).sub(camera.position); tmp.y = 0;
    if (tmp.lengthSq() > 1e-6) CAM.setForward(tmp.x, tmp.z);
  });
  return <OrbitControls ref={controls} enableDamping dampingFactor={0.075} enablePan={false}
    minDistance={2.4} maxDistance={40} maxPolarAngle={Math.PI / 1.9} onStart={() => { posed.current = true; }} />;
}

// ── SKY, LIGHT & NIGHT (wall-clock: every client agrees) ─────
function Sky() {
  const { scene, camera } = useThree();
  const sun = useRef(), amb = useRef(), moonL = useRef(), starsG = useRef(), moonM = useRef(), starMat = useRef();
  const cDay = useMemo(() => new THREE.Color("#a9d4e6"), []);
  const cNight = useMemo(() => new THREE.Color("#060818"), []);
  const cDusk = useMemo(() => new THREE.Color("#d9825a"), []);
  const tmpC = useMemo(() => new THREE.Color(), []);
  // THE BLOOD MOON (2026-10-31): on a blood night the fog (and the sky behind it - they're one colour) eases toward BLOOD.FOG and the moon goes red
  const cBlood = useMemo(() => new THREE.Color(BLOOD.FOG), []), cMoon = useMemo(() => new THREE.Color("#f4f1e0"), []), cMoonB = useMemo(() => new THREE.Color(BLOOD.MOON), []);
  const blood = useRef(0);                              // 0..1, eased (refs only - PERF LAW)
  const starPos = useMemo(() => {
    const r = mulberry32(9), a = new Float32Array(420 * 3);
    for (let i = 0; i < 420; i++) {
      const th = r() * Math.PI * 2, ph = Math.acos(0.05 + r() * 0.95), R = 150;
      a[i * 3] = R * Math.sin(ph) * Math.cos(th); a[i * 3 + 1] = R * Math.cos(ph); a[i * 3 + 2] = R * Math.sin(ph) * Math.sin(th);
    }
    return a;
  }, []);
  useEffect(() => { scene.fog = new THREE.Fog("#a9d4e6", 14, 78); return () => { scene.fog = null; }; }, [scene]);
  useFrame((_, dtRaw) => {
    const now = Date.now(), ph = dayPhase(now);
    const L = ph < 0.6 ? Math.min(1, Math.sin(Math.PI * (ph / 0.6)) * 1.9) : 0;
    const dusk = THREE.MathUtils.smoothstep(ph, 0.46, 0.58) * (1 - THREE.MathUtils.smoothstep(ph, 0.6, 0.66));
    blood.current += ((isBloodMoon(now) ? 1 : 0) - blood.current) * Math.min(1, Math.min(dtRaw, 0.1) * 0.6);
    tmpC.copy(cNight).lerp(cDay, L).lerp(cDusk, dusk * 0.45);
    if (blood.current > 0.001) tmpC.lerp(cBlood, blood.current);
    scene.background = scene.background && scene.background.isColor ? scene.background.copy(tmpC) : tmpC.clone();
    if (scene.fog) scene.fog.color.copy(tmpC);
    const a = (Math.min(ph, 0.6) / 0.6) * Math.PI;
    if (sun.current) { sun.current.position.set(Math.cos(a) * 60, Math.sin(a) * 60 + 6, 22); sun.current.intensity = 0.1 + L * 1.7; }
    if (amb.current) amb.current.intensity = 0.28 + L * 0.55;
    if (moonL.current) moonL.current.intensity = (1 - L) * 0.55;
    if (starMat.current) starMat.current.opacity = Math.max(0, 1 - L * 1.4);
    if (starsG.current) starsG.current.position.copy(camera.position);
    if (moonM.current) { moonM.current.visible = L < 0.6; moonM.current.position.set(camera.position.x - 70, camera.position.y + 80, camera.position.z - 90); moonM.current.material.color.copy(cMoon).lerp(cMoonB, blood.current); }
  });
  return (
    <>
      <ambientLight ref={amb} color="#b9c4ff" intensity={0.5} />
      <directionalLight ref={sun} color="#fff1d0" intensity={1.2} />
      <directionalLight ref={moonL} color="#7d92ff" position={[-30, 50, -20]} intensity={0.3} />
      <group ref={starsG}>
        <points frustumCulled={false}>
          <bufferGeometry><bufferAttribute attach="attributes-position" args={[starPos, 3]} /></bufferGeometry>
          <pointsMaterial ref={starMat} color="#ffffff" size={1.4} sizeAttenuation={false} transparent depthWrite={false} fog={false} />
        </points>
      </group>
      <mesh ref={moonM}><sphereGeometry args={[7, 16, 16]} /><meshBasicMaterial color="#f4f1e0" fog={false} /></mesh>
    </>
  );
}

// ── GROUND, TREES, RUINS ─────────────────────────────────────
function Ground() {
  return (
    <group>
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.02, 0]} receiveShadow>
        <circleGeometry args={[WORLD.R + 60, 48]} /><meshStandardMaterial color="#344a29" roughness={1} />
      </mesh>
      {/* the still's yard: packed dirt inside the lantern line */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.005, 0]}>
        <circleGeometry args={[WORLD.YARD_R, 40]} /><meshStandardMaterial color="#6b5238" roughness={1} />
      </mesh>
      {PLACES.filter((p) => !p.eyes).map((p) => (
        <mesh key={p.id} rotation={[-Math.PI / 2, 0, 0]} position={[p.x, 0.01, p.z]}>
          <circleGeometry args={[p.r, 28]} />
          <meshStandardMaterial color={INGREDIENTS[p.yields[0]].color} transparent opacity={0.16} roughness={1} />
        </mesh>
      ))}
    </group>
  );
}

function Trees() {
  const trunk = useRef(), crownA = useRef(), crownB = useRef();
  useLayoutEffect(() => {
    const m = new THREE.Matrix4(), q = new THREE.Quaternion(), pos = new THREE.Vector3(), sc = new THREE.Vector3(), col = new THREE.Color();
    GEN.trees.forEach((t, i) => {
      m.compose(pos.set(t.x, 1.0 * t.s, t.z), q, sc.set(0.26 * t.s, 2.0 * t.s, 0.26 * t.s)); trunk.current.setMatrixAt(i, m);
      m.compose(pos.set(t.x, 2.7 * t.s, t.z), q, sc.set(1.5 * t.s, 2.6 * t.s, 1.5 * t.s)); crownA.current.setMatrixAt(i, m);
      m.compose(pos.set(t.x, 4.1 * t.s, t.z), q, sc.set(1.1 * t.s, 2.0 * t.s, 1.1 * t.s)); crownB.current.setMatrixAt(i, m);
      col.setHSL(0.29 + t.h * 0.06, 0.42, 0.14 + t.h * 0.1);
      crownA.current.setColorAt(i, col); crownB.current.setColorAt(i, col);
    });
    [trunk, crownA, crownB].forEach((r) => { r.current.instanceMatrix.needsUpdate = true; if (r.current.instanceColor) r.current.instanceColor.needsUpdate = true; });
  }, []);
  const n = GEN.trees.length;
  return (
    <group>
      <instancedMesh ref={trunk} args={[null, null, n]}><cylinderGeometry args={[1, 1.25, 1, 6]} /><meshStandardMaterial color="#4a3422" roughness={1} /></instancedMesh>
      <instancedMesh ref={crownA} args={[null, null, n]}><coneGeometry args={[1, 1, 7]} /><meshStandardMaterial color="#ffffff" roughness={0.95} /></instancedMesh>
      <instancedMesh ref={crownB} args={[null, null, n]}><coneGeometry args={[1, 1, 7]} /><meshStandardMaterial color="#ffffff" roughness={0.95} /></instancedMesh>
    </group>
  );
}

function RuinStones() {
  return (
    <group>
      {GEN.stones.map((s, i) => (
        <mesh key={i} position={[s.x, s.s * 0.9, s.z]} rotation={[0, s.ry, 0]}>
          <boxGeometry args={[0.7, s.s * 1.8, 0.7]} /><meshStandardMaterial color="#6a6f7a" roughness={1} />
        </mesh>
      ))}
    </group>
  );
}

// ── THE YARD (placeholders for the Blender kit) ──────────────
function Lanterns() {
  const pts = useMemo(() => { const N = Math.round(LAYOUT.lanternCount * WORLD.YARD_R / 9.5); return Array.from({ length: N }, (_, i) => {   // worldgen's count: the same gap at any camp size (rebuilt per camp by the key)
    const a = (i / N) * Math.PI * 2;
    return [Math.cos(a) * WORLD.YARD_R, Math.sin(a) * WORLD.YARD_R];
  }); }, []);
  return (
    <group>
      {pts.map(([x, z], i) => (
        <group key={i} position={[x, 0, z]}>
          <mesh position={[0, 0.7, 0]}><cylinderGeometry args={[0.05, 0.06, 1.4, 6]} /><meshStandardMaterial color="#3a2a1c" /></mesh>
          <mesh position={[0, 1.5, 0]}><sphereGeometry args={[0.16, 10, 10]} /><meshStandardMaterial color="#ffd27a" emissive="#ffb347" emissiveIntensity={2.4} /></mesh>
        </group>
      ))}
    </group>
  );
}

// THE STILL's look (2026-10-23) - WILD.fx.still is a phase name from App:
// idle | fore | heads | hearts | turning | tails | dry | clog | pop | boom | broken
const STILL_LOOK = {
  idle:    { glow: 2.2, col: "#ff8a33", steam: 0,   smoke: false },
  fore:    { glow: 7,   col: "#ff8a33", steam: 0.5, pulse: true },
  heads:   { glow: 7,   col: "#ff8a33", steam: 0.5, pulse: true },
  hearts:  { glow: 6,   col: "#f6d58a", steam: 0.5 },
  turning: { glow: 6,   col: "#f6d58a", steam: 0.5, pulse: true },
  tails:   { glow: 7,   col: "#ff5a2a", steam: 0.5, pulse: true },
  dry:     { glow: 5,   col: "#9fe08a", steam: 0.15 },
  clog:    { glow: 8,   col: "#ff3020", steam: 0,   flicker: true, hiss: true },
  pop:     { glow: 9,   col: "#ffffff", steam: 0.95, big: 1.8 },
  boom:    { glow: 16,  col: "#ff7a1a", steam: 0.95, big: 3.2, smoke: true },
  broken:  { glow: 1.6, col: "#a03020", steam: 0.45, smoke: true },
};
// k = which still (0 = the first; 1 = THE SECOND STILL, a yard upgrade 2026-10-31: same body, its own look from WILD.fx.still2).
// seized = the revenuers hauled it off: no body, no glow, no steam (the first still's light stays MOUNTED at 0 - a light that comes
// and goes recompiles every shader). The second still has NO light of its own (the light-budget law): its firebox glow is an
// additive sprite that breathes the same way.
const STILL_OFF = { glow: 0, col: "#000000", steam: 0 };
function Still({ bare, k = 0, seized = false }) {   // bare = the kit's still model stands here; keep only the live glow + steam
  const glow = useRef(), cap = useRef(), steam = useRef(), spr = useRef();
  const tex = useMemo(() => (k ? glowTexture() : null), [k]);
  useEffect(() => () => { if (tex) tex.dispose(); }, [tex]);
  useFrame(({ clock }) => {
    const L = seized ? STILL_OFF : STILL_LOOK[WILD.fx[k ? "still2" : "still"]] || STILL_LOOK.idle, t = clock.elapsedTime;
    const I = L.flicker ? L.glow * (0.4 + 0.6 * (Math.sin(t * 23) > 0 ? 1 : 0.3)) : L.pulse ? L.glow + Math.sin(t * 5) * 1.2 : L.glow;
    if (glow.current) { glow.current.intensity = I; glow.current.color.set(L.col); }
    if (spr.current) { spr.current.visible = I > 0.05; spr.current.material.color.set(L.col); spr.current.material.opacity = Math.min(0.9, 0.18 + I * 0.07); spr.current.scale.setScalar(1.3 + I * 0.12); }
    if (cap.current) cap.current.material.emissiveIntensity = L.pulse ? 0.5 + Math.sin(t * 5) * 0.2 : L.steam ? 0.35 : 0.05;
    if (steam.current) steam.current.children.forEach((c, i) => {
      const k = ((t * (L.big ? 0.9 : 0.5) + i * 0.2) % 1), on = L.steam > 0 || !!L.hiss;   // a real boolean: three only hides visible === false (undefined drew six white balls - fixed 2026-10-24)
      c.visible = on;
      if (!on) return;
      const big = L.big || 1, side = L.hiss ? 1 : 0;      // a clog hisses sideways out of the cap
      c.position.set(side ? (i % 2 ? 1 : -1) * (0.6 + k * 0.9) : 0.15 * big * Math.sin(t + i), (bare ? 2.75 : 2.9) + k * (side ? 0.3 : 2.4 * big), 0.15 * big * Math.cos(t + i));
      c.scale.setScalar(side ? 0.08 + k * 0.15 : (0.18 + k * 0.5) * big);
      c.material.opacity = (1 - k) * (L.hiss ? 0.6 : L.steam);
      c.material.color.set(L.smoke ? (i % 2 ? "#2a2422" : "#44403c") : "#e9e9e9");
    });
  });
  const [sx, sz] = k ? LAYOUT.still2 : LAYOUT.still;
  return (
    <group position={[sx, 0, sz]}>
      {!bare && !seized && <>
      <mesh position={[0, 0.45, 0]}><boxGeometry args={[1.6, 0.9, 1.3]} /><meshStandardMaterial color="#4a3b33" roughness={1} /></mesh>
      <mesh position={[0, 1.55, 0]}><cylinderGeometry args={[0.7, 0.85, 1.3, 16]} /><meshStandardMaterial color="#b8693a" metalness={0.7} roughness={0.35} /></mesh>
      <mesh ref={cap} position={[0, 2.5, 0]}><sphereGeometry args={[0.62, 16, 12]} /><meshStandardMaterial color="#c4753f" emissive="#ff8a33" emissiveIntensity={0.05} metalness={0.7} roughness={0.3} /></mesh>
      <mesh position={[0.95, 2.7, 0]} rotation={[0, 0, -0.9]}><cylinderGeometry args={[0.09, 0.09, 1.8, 8]} /><meshStandardMaterial color="#b8693a" metalness={0.7} roughness={0.35} /></mesh>
      <mesh position={[1.6, 1.9, 0]}><cylinderGeometry args={[0.09, 0.09, 1.2, 8]} /><meshStandardMaterial color="#b8693a" metalness={0.7} roughness={0.35} /></mesh>
      <mesh position={[1.6, 1.15, 0]}><cylinderGeometry args={[0.25, 0.22, 0.45, 10]} /><meshStandardMaterial color="#9cc7d6" transparent opacity={0.7} /></mesh>
      </>}
      {k ? <sprite ref={spr} position={bare ? [0, 0.5, 1.0] : [0, 0.5, 0.8]}>
        <spriteMaterial map={tex} color="#ff8a33" transparent depthWrite={false} blending={THREE.AdditiveBlending} toneMapped={false} />
      </sprite>
        : <pointLight ref={glow} position={bare ? [0, 0.45, 1.1] : [0, 1.2, 1.0]} intensity={2.2} distance={9} decay={1.6} color="#ff8a33" />}
      <group ref={steam}>{[0, 1, 2, 3, 4, 5].map((i) => (
        <mesh key={i} visible={false}><sphereGeometry args={[1, 8, 8]} /><meshBasicMaterial transparent depthWrite={false} /></mesh>
      ))}</group>
    </group>
  );
}

// THE RIG's bought parts, standing by the still (PLACEHOLDER primitives until the Blender pass - the seam is RIG_SPOTS)
const COPPER = <meshStandardMaterial color="#b8693a" metalness={0.7} roughness={0.35} />;
function RigParts({ rig, k = 0 }) {   // k = 1: the same parts on THE SECOND STILL (the spots shifted by still2 - still)
  if (!rig) return null;
  if (k) return <group position={[LAYOUT.still2[0] - LAYOUT.still[0], 0, LAYOUT.still2[1] - LAYOUT.still[1]]}><RigParts rig={rig} /></group>;
  const [sx, sz] = LAYOUT.still, [tx, tz] = RIG_SPOTS.thumper, [cx, cz] = RIG_SPOTS.column;
  return (
    <group>
      {rig.thumper === "thumper" && <group position={[tx, 0, tz]}>
        <mesh position={[0, 0.45, 0]}><cylinderGeometry args={[0.36, 0.4, 0.9, 14]} /><meshStandardMaterial color="#7a5230" roughness={1} /></mesh>
        {[0.15, 0.75].map((y) => <mesh key={y} position={[0, y, 0]}><cylinderGeometry args={[0.41, 0.41, 0.06, 14]} />{COPPER}</mesh>)}
        <mesh position={[-0.45, 1.25, -0.5]} rotation={[0.9, 0, 0.6]}><cylinderGeometry args={[0.05, 0.05, 1.5, 6]} />{COPPER}</mesh>
      </group>}
      {rig.head === "column" && <group position={[cx, 0, cz]}>
        <mesh position={[0, 3.6, 0]}><cylinderGeometry args={[0.2, 0.22, 1.6, 12]} />{COPPER}</mesh>
        {[3.1, 3.6, 4.1].map((y) => <mesh key={y} position={[0, y, 0]}><cylinderGeometry args={[0.25, 0.25, 0.05, 12]} />{COPPER}</mesh>)}
      </group>}
      {rig.cond === "shotgun" && <mesh position={[sx + 1.95, 1.6, sz]} rotation={[0, 0, Math.PI / 2]}><cylinderGeometry args={[0.28, 0.28, 1.2, 14]} />{COPPER}</mesh>}
      {rig.cond === "radiator" && <group position={[sx + 1.95, 1.35, sz + 0.1]}>
        <mesh><boxGeometry args={[0.9, 0.7, 0.12]} /><meshStandardMaterial color="#5f6266" metalness={0.5} roughness={0.7} /></mesh>
        {[-0.3, -0.15, 0, 0.15, 0.3].map((x) => <mesh key={x} position={[x, 0, 0.07]}><boxGeometry args={[0.04, 0.66, 0.03]} /><meshStandardMaterial color="#3d3f42" /></mesh>)}
        <mesh position={[0.2, -0.2, 0.09]}><boxGeometry args={[0.18, 0.12, 0.02]} /><meshStandardMaterial color="#7a2a20" /></mesh>
      </group>}
    </group>
  );
}

function Campfire({ bare }) {   // bare = the kit's stones + logs stand here; keep only the dancing flames
  const flames = useRef();
  useFrame(({ clock }) => {
    const t = clock.elapsedTime;
    if (flames.current) flames.current.children.forEach((c, i) => { c.scale.set(1, 0.8 + Math.sin(t * 9 + i * 2) * 0.25, 1); c.rotation.y = t * (1 + i * 0.4); });
  });
  const [cx, cz] = LAYOUT.campfire;
  return (
    <group position={[cx, 0, cz]}>
      {!bare && [0, 1, 2, 3, 4, 5, 6].map((i) => { const a = (i / 7) * Math.PI * 2; return (
        <mesh key={i} position={[Math.cos(a) * 0.7, 0.12, Math.sin(a) * 0.7]}><sphereGeometry args={[0.16, 8, 6]} /><meshStandardMaterial color="#5b5b60" roughness={1} /></mesh>
      ); })}
      {!bare && [0, 1, 2].map((i) => <mesh key={i} position={[0, 0.16, 0]} rotation={[Math.PI / 2 - 0.25, 0, (i / 3) * Math.PI * 2]}><cylinderGeometry args={[0.07, 0.07, 0.9, 6]} /><meshStandardMaterial color="#4a3422" roughness={1} /></mesh>)}
      {bare && <pointLight position={[0, 0.7, 0]} color="#ff9a3a" intensity={3} distance={7} decay={1.7} />}
      <group ref={flames}>
        {[0.34, 0.24, 0.16].map((r, i) => (
          <mesh key={i} position={[0, 0.3 + i * 0.12, 0]}><coneGeometry args={[r, 0.7 - i * 0.12, 7]} /><meshStandardMaterial color="#ffb347" emissive={i ? "#ff7a1a" : "#ff9a2a"} emissiveIntensity={2.2} transparent opacity={0.92} /></mesh>
        ))}
      </group>
    </group>
  );
}

function Yard({ kit, seized, still2, shackS = 1 }) {   // seized: the still(s) are gone; still2: THE SECOND STILL stands; shackS: the shack's scale (YARD_UP)
  const [bx, bz] = LAYOUT.board, [hx, hz] = LAYOUT.shack;
  // every other yard piece is a kit model (Woods3D). One FIXED warm yard light (high, just off-centre, 12 m reach)
  // so the shack + board don't fall into black at dusk (2026-10-24 critic pass); never a light per lantern.
  if (kit) return (<group><Campfire bare /><Still bare seized={seized} />{still2 ? <Still bare k={1} seized={seized} /> : null}
    <pointLight position={[(bx + hx) * 0.35, 2.6, (bz + hz) * 0.35]} color="#ffcf8a" intensity={2.2} distance={12} decay={1.4} /></group>);
  return (
    <group>
      <Campfire />
      <Still seized={seized} />
      {still2 ? <Still k={1} seized={seized} /> : null}
      {/* mash tub + a couple of barrels (Blender kit later) */}
      <mesh position={[-3.4, 0.5, -1.2]}><cylinderGeometry args={[0.95, 0.8, 1.0, 14]} /><meshStandardMaterial color="#6d4a2b" roughness={1} /></mesh>
      {[[-2.2, -4.2], [-1.4, -4.6], [2.6, -5.0]].map(([x, z], i) => (
        <mesh key={i} position={[x, 0.55, z]}><cylinderGeometry args={[0.45, 0.5, 1.1, 12]} /><meshStandardMaterial color="#7a5230" roughness={1} /></mesh>
      ))}
      {/* the shack */}
      <group position={[hx, 0, hz]} scale={shackS}>
        <mesh position={[0, 1.2, 0]}><boxGeometry args={[3.4, 2.4, 3.0]} /><meshStandardMaterial color="#6e5236" roughness={1} /></mesh>
        <mesh position={[0, 3.0, 0]} rotation={[0, Math.PI / 4, 0]}><coneGeometry args={[2.9, 1.6, 4]} /><meshStandardMaterial color="#4a2f22" roughness={1} /></mesh>
        <mesh position={[0, 0.85, 1.52]}><boxGeometry args={[0.9, 1.7, 0.06]} /><meshStandardMaterial color="#2a1c12" /></mesh>
      </group>
      {/* Tome Contract board */}
      <group position={[bx, 0, bz]}>
        {[-0.7, 0.7].map((dx) => <mesh key={dx} position={[dx, 1.0, 0]}><cylinderGeometry args={[0.07, 0.08, 2.0, 6]} /><meshStandardMaterial color="#4a3422" /></mesh>)}
        <mesh position={[0, 1.55, 0]}><boxGeometry args={[1.7, 0.95, 0.08]} /><meshStandardMaterial color="#8a6a42" roughness={1} /></mesh>
        <mesh position={[0, 1.55, 0.05]}><planeGeometry args={[1.1, 0.6]} /><meshStandardMaterial color="#efe3bd" emissive="#e9cf8a" emissiveIntensity={0.25} /></mesh>
      </group>
      <Lanterns />
    </group>
  );
}

// ── THE RUN's station FX: steam off a cooking mash, bubbles in a working barrel (green when ready) ──
// One useFrame, refs only; reads the game state through gameRef (no React in the frame).
function StationFx({ gameRef }) {
  const steam = useRef(), bub = useRef([]);
  useFrame(({ clock }) => {
    const g = gameRef.current, t = clock.elapsedTime, now = Date.now(); if (!g) return;
    const tp = tubPhase(g.tub, now);
    if (steam.current) steam.current.children.forEach((c, i) => {
      const k = (t * 0.45 + i * 0.25) % 1; c.visible = tp !== "empty";
      c.position.set(0.3 * Math.sin(t * 0.7 + i * 2), 1.05 + k * 1.6, 0.3 * Math.cos(t * 0.6 + i));
      c.scale.setScalar(0.15 + k * 0.45); c.material.opacity = (1 - k) * (tp === "cooking" ? 0.45 : 0.18);
    });
    STATIONS.barrels.forEach((_, i) => {
      const grp = bub.current[i]; if (!grp) return;
      const ph = barrelPhase(g.barrels[i], now); grp.visible = ph !== "empty";
      grp.children.forEach((c, j) => {
        const k = (t * (ph === "ready" ? 0.25 : 0.6) + j * 0.31) % 1;
        c.position.set(0.18 * Math.sin(j * 2.3 + t), 1.2 + k * 0.5, 0.18 * Math.cos(j * 1.7 + t));
        c.scale.setScalar(0.05 + 0.04 * Math.sin(k * Math.PI)); c.material.color.set(ph === "ready" ? "#8fd88a" : "#f2c060");
      });
    });
  });
  return (
    <group>
      <group ref={steam} position={[STATIONS.tub[0], 0, STATIONS.tub[1]]}>{[0, 1, 2, 3].map((i) => (
        <mesh key={i} visible={false}><sphereGeometry args={[1, 8, 8]} /><meshBasicMaterial color="#eeeeee" transparent depthWrite={false} /></mesh>
      ))}</group>
      {STATIONS.barrels.map(([x, z], i) => (
        <group key={i} ref={(el) => { bub.current[i] = el; }} position={[x, 0, z]} visible={false}>
          {[0, 1, 2, 3, 4].map((j) => <mesh key={j}><sphereGeometry args={[1, 8, 6]} /><meshBasicMaterial color="#f2c060" toneMapped={false} /></mesh>)}
        </group>
      ))}
    </group>
  );
}

// ── THE TUTORIAL BEACON: a tall soft pillar (it clears the pines), a pulsing ring and a bobbing arrow
// over wherever the current step wants you. Reads the game through gameRef; tutorial.js says where.
function TutBeacon({ gameRef, posRef }) {
  const g = useRef(), ring = useRef(), arrow = useRef(), pillar = useRef();
  useFrame(({ clock }) => {
    const s = gameRef.current, grp = g.current; if (!grp || !s) return;
    const st = !s.tut.done ? stepAt(s) : null, at = st && !st.card ? targetOf(st, s, Date.now()) : null;
    grp.visible = !!at; if (!at) return;
    grp.position.set(at[0], 0, at[1]);
    const t = clock.elapsedTime, p = posRef.current, d = Math.hypot(p.x - at[0], p.z - at[1]);
    const k = 0.5 + 0.5 * Math.sin(t * 2.4);
    if (ring.current) { ring.current.scale.setScalar(1 + 0.25 * k); ring.current.material.opacity = 0.35 + 0.35 * (1 - k); }
    if (arrow.current) { arrow.current.position.y = (st.target === "still" ? 3.6 : 2.6) + 0.25 * Math.sin(t * 3); arrow.current.rotation.y = t * 1.2; }   // clear of the still's cap
    if (pillar.current) pillar.current.material.opacity = Math.min(0.3, 0.06 + d * 0.012);   // fades as you arrive
  });
  return (
    <group ref={g} visible={false}>
      <mesh ref={pillar} position={[0, 15, 0]}><cylinderGeometry args={[0.22, 0.32, 30, 12, 1, true]} /><meshBasicMaterial color="#f6d58a" transparent opacity={0.25} depthWrite={false} side={THREE.DoubleSide} toneMapped={false} fog={false} /></mesh>
      <mesh ref={ring} rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.04, 0]}><ringGeometry args={[1.0, 1.18, 40]} /><meshBasicMaterial color="#f6d58a" transparent opacity={0.5} depthWrite={false} toneMapped={false} /></mesh>
      <mesh ref={arrow} position={[0, 3.1, 0]} rotation={[Math.PI, 0, 0]}><coneGeometry args={[0.22, 0.45, 4]} /><meshBasicMaterial color="#f6d58a" toneMapped={false} /></mesh>
    </group>
  );
}

// ── FORAGING ORBS - one useFrame for all of them ─────────────
function ForageNodes() {
  const refs = useRef([]);
  useFrame(({ clock }) => {
    const now = Date.now(), night = isNight(now), t = clock.elapsedTime;
    GEN.nodes.forEach((n, i) => {
      const g = refs.current[i]; if (!g) return;
      const at = WILD.pickedAt[n.id];
      const avail = !(at && now - at < TUNING.NODE_RESPAWN_S * 1000);
      const open = avail && (!n.night || night) && (!n.eyes || WILD.eyes);
      const target = open ? 1 : (avail && !n.eyes ? 0.28 : 0);        // a sleeping moonbloom shows as a dim bud
      const s = g.scale.x + (target - g.scale.x) * 0.15;
      g.scale.setScalar(Math.max(0.0001, s));
      g.position.y = 0.55 + Math.sin(t * 2 + i) * 0.08;
      g.rotation.y = t * 0.7 + i;
    });
  });
  return (
    <group>
      {GEN.nodes.map((n, i) => {
        const ing = INGREDIENTS[n.ing];
        return (
          <group key={n.id} position={[n.x, 0, n.z]}>
            <group ref={(el) => { refs.current[i] = el; }} position={[0, 0.55, 0]}>
              <mesh><octahedronGeometry args={[ing.rare ? 0.3 : 0.24, 0]} /><meshStandardMaterial color={ing.color} emissive={ing.color} emissiveIntensity={ing.rare ? 1.4 : 0.7} roughness={0.5} /></mesh>
            </group>
            {n.eyes ? null : <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.02, 0]}><ringGeometry args={[0.28, 0.42, 20]} /><meshBasicMaterial color={ing.color} transparent opacity={0.55} depthWrite={false} /></mesh>}
          </group>
        );
      })}
    </group>
  );
}

// ── THE TOWNSFOLK AT THE BOARD (2026-10-30, ROADMAP 4 - the endless board's slice 2) ──
// Whoever is waiting on the board (and whoever you've taken a job from) stands beside it in their OWN Toonz body - the Pier's kits
// (moonshineFolk3D.jsx; caP'n 2026-10-30: the capsule stand-ins are gone, "use the unstable pierre toonz").
// Walk-through (no colliders), no labels (the board panel names them), no new lights. They come and go with the board.
const TOWN_SPOTS = [[-1.55, 0.55], [1.55, 0.55], [-2.35, 1.15], [2.35, 1.15], [-2.9, 2.0], [2.9, 2.0]];   // [side, toward the yard] metres from the board
function Townsfolk({ game }) {
  const folk = useMemo(() => {
    if (!game || !game.contracts) return [];
    const seen = new Set(), out = [];
    for (const c of [...acceptedList(game), ...offeredList(game)]) {
      const p = c.species && personOf(c.species);
      if (!p || seen.has(p.id)) continue;
      seen.add(p.id); out.push(p);
      if (out.length >= TOWN_SPOTS.length) break;
    }
    return out;
  }, [game && game.contracts, game && game.board, game && game.ledger, game && game.crew, game && game.specs, game && game.stats]);   // eslint-disable-line react-hooks/exhaustive-deps -- the board's inputs
  const [bx, bz] = LAYOUT.board, d = Math.hypot(bx, bz) || 1, fx = -bx / d, fz = -bz / d, sx = -fz, sz = fx;   // forward = toward the yard centre
  return (
    <group>
      {folk.map((p, i) => {
        const [a, b] = TOWN_SPOTS[i], x = bx + sx * a + fx * b, z = bz + sz * a + fz * b, ry = Math.atan2(-x, -z);
        return (
          <ToonzPerson key={p.id} person={p} position={[x, 0, z]} rotationY={ry} />
        );
      })}
    </group>
  );
}

// ── THE UNSTABLE SIGHTS (2026-10-30) — PLACEHOLDER looks until the visual pass ──
// The Stray Wisp: a bobbing pale-blue glow under a thin pillar of light (so you can find it from the yard).
// A sight: a slowly turning hollow star (✧-ish: a ring + a spike) in its own colour. Seen ones are gone.
// One useFrame; nothing is drawn with the Eyes shut. No new lights (emissive / unlit only - the light-budget law).
// which Stray Wisp is out there: the crew's shared count on a server (moonshineWoods useSharedWisp), else my own
const liveWisp = (g) => (WILD.sid && WILD.wispN != null ? WILD.wispN : (g.stats && g.stats.wisps) || 0);
function UnstableSights({ gameRef }) {
  const root = useRef(), wisp = useRef(), orb = useRef(), sights = useRef([]);
  useFrame(({ clock }) => {
    const g = gameRef.current, t = clock.elapsedTime, on = WILD.eyes && !!g;
    if (!root.current) return;
    root.current.visible = on;
    if (!on) return;
    const carrying = g.bag && g.bag.wisp > 0, w = wispAt(liveWisp(g));
    if (wisp.current) { wisp.current.visible = !carrying && !(WILD.sid && WILD.wispN == null); wisp.current.position.set(w.x, 0, w.z); }
    if (orb.current) { orb.current.position.y = 1.2 + Math.sin(t * 2.4) * 0.18; orb.current.scale.setScalar(1 + Math.sin(t * 5) * 0.08); }
    GEN.sights.forEach((sg, i) => {
      const m = sights.current[i]; if (!m) return;
      m.visible = !(g.sights && g.sights[sg.id]);
      m.rotation.y = t * 0.6 + i; m.position.y = 1.5 + Math.sin(t * 1.3 + i) * 0.12;
    });
  });
  return (
    <group ref={root} visible={false}>
      <group ref={wisp}>
        <mesh ref={orb}><sphereGeometry args={[0.32, 16, 12]} /><meshBasicMaterial color={INGREDIENTS.wisp.color} toneMapped={false} /></mesh>
        <mesh position={[0, 7, 0]}><cylinderGeometry args={[0.08, 0.08, 14, 8, 1, true]} /><meshBasicMaterial color={INGREDIENTS.wisp.color} transparent opacity={0.28} depthWrite={false} toneMapped={false} /></mesh>
      </group>
      {GEN.sights.map((sg, i) => (
        <group key={sg.id} position={[sg.x, 1.5, sg.z]} ref={(el) => { sights.current[i] = el; }}>
          <mesh><torusGeometry args={[0.42, 0.05, 8, 24]} /><meshBasicMaterial color={sg.color} toneMapped={false} /></mesh>
          <mesh rotation={[0, 0, Math.PI / 4]}><octahedronGeometry args={[0.2, 0]} /><meshBasicMaterial color={sg.color} toneMapped={false} /></mesh>
        </group>
      ))}
    </group>
  );
}

// ── THE PHANTOM BEASTS (PRIME law 14) ────────────────────────
// PLACEHOLDER BODY. [BORROW] The real ones are Terror's Spectral Tiger /
// Shadow Fox family (gamemaster/models/ieBeingModels.jsx) - swap this one
// component when the scaffold is proven; nothing else changes.
function PhantomBeast({ refCb }) {
  return (
    <group ref={refCb}>
      <mesh position={[0, 0.62, 0]}><boxGeometry args={[0.7, 0.62, 1.5]} /><meshStandardMaterial color="#2b2147" emissive="#5a2fbf" emissiveIntensity={0.55} transparent opacity={0.88} /></mesh>
      <mesh position={[0, 0.82, 0.88]}><boxGeometry args={[0.52, 0.5, 0.55]} /><meshStandardMaterial color="#2b2147" emissive="#5a2fbf" emissiveIntensity={0.55} transparent opacity={0.88} /></mesh>
      <mesh position={[-0.14, 0.9, 1.17]}><sphereGeometry args={[0.07, 8, 8]} /><meshBasicMaterial color="#ff4466" /></mesh>
      <mesh position={[0.14, 0.9, 1.17]}><sphereGeometry args={[0.07, 8, 8]} /><meshBasicMaterial color="#ff4466" /></mesh>
      <mesh position={[0, 0.7, -1.0]} rotation={[0.5, 0, 0]}><cylinderGeometry args={[0.06, 0.12, 0.9, 6]} /><meshStandardMaterial color="#2b2147" emissive="#5a2fbf" emissiveIntensity={0.7} /></mesh>
      {[[-0.26, 0.5], [0.26, 0.5], [-0.26, -0.5], [0.26, -0.5]].map(([x, z], i) => (
        <mesh key={i} position={[x, 0.2, z]}><boxGeometry args={[0.16, 0.4, 0.16]} /><meshStandardMaterial color="#1c1530" /></mesh>
      ))}
    </group>
  );
}

function Beasts({ posRef, bagRef, cb, share, others }) {
  const refs = useRef([]);
  const rng = useRef(mulberry32(4242));
  const st = useMemo(() => GEN.beasts.map((b) => ({ zone: b.zone, x: b.hx, z: b.hz, hx: b.hx, hz: b.hz, yaw: 0, wx: b.hx, wz: b.hz, wt: 0, chasing: false })), []);
  // CO-OP step 3 (moonshineWoods.js): the keeper runs ONE pack for everyone; a guest follows it (and is caught by it)
  const shareRef = useRef(share); shareRef.current = share;
  const sid = share && share.sid, mode = share ? share.mode : "solo", me = share && share.uid;
  const feed = useBeastFeed(sid, mode === "guest");
  const heat = useRef({});                              // seconds outside the lanterns, per brewer (key "me" or a friend's uid)
  const grace = useRef({});                             // keeper: a friend just caught gets the same safe seconds you do
  const lastSend = useRef(0);
  const catchMe = useCallback(() => {                   // one hit and you're done (PRIME law 6) - back to the yard, bag lost, a few safe seconds
    const p = posRef.current, now = Date.now();
    p.set(WORLD.SPAWN[0], 0, WORLD.SPAWN[1]);
    WILD.invulnUntil = now + BEAST.GRACE_MS;
    WILD.snapCam = true;
    bagRef.current = 0;                                // lost NOW - the Controller must not bank it this same frame
    cb.onCaught();
  }, [posRef, bagRef, cb]);
  // the keeper sees me ~0.1 s late and keeps a quiet friend for a minute: a catch only counts if I'm really out there
  useCaught(mode === "guest" ? sid : null, me, useCallback(() => {
    const p = posRef.current;
    if (document.hidden || Math.hypot(p.x, p.z) < WORLD.YARD_R + 1 || Date.now() < WILD.invulnUntil) return;
    catchMe();
  }, [posRef, catchMe]));
  useFrame((_, dtRaw) => {
    const dt = Math.min(dtRaw, 0.05), now = Date.now(), p = posRef.current, night = isNight(now), sh = shareRef.current;
    const bloodK = isBloodMoon(now) ? BLOOD.BEAST_K : 1;  // THE BLOOD MOON's seam (BEAST_K 1 = no change)
    // GUEST: follow the keeper's pack while it's fresh (her beasts decide the catches)
    const f = feed.current;
    if (sh && sh.mode === "guest" && f && f.k === sh.keeperUid && now - f.at < BEAST_STALE_MS) {
      const k = 1 - Math.exp(-dt * 8);
      st.forEach((b, i) => {
        const r = f.list[i]; if (!r) return;
        if (Math.hypot(r[0] - b.x, r[1] - b.z) > 5) { b.x = r[0]; b.z = r[1]; }      // sent home: snap, don't slide across the map
        else { b.x += (r[0] - b.x) * k; b.z += (r[1] - b.z) * k; }
        b.yaw = r[2]; b.chasing = !!r[3];
        const g = refs.current[i]; if (g) { g.position.set(b.x, 0, b.z); g.rotation.y = b.yaw; }
      });
      return;
    }
    const keeping = sh && sh.mode === "keeper";
    const aggro = BEAST.AGGRO;   // 999 = it knows the instant you step out of the yard into its zone
    // who's out there: me, and (keeping the yard) every friend past the lanterns
    const prey = [{ id: null, eyes: WILD.eyes, x: p.x, z: p.z, out: Math.hypot(p.x, p.z) > WORLD.YARD_R && now > WILD.invulnUntil }];
    if (keeping && others) for (const id of others.ids) {
      const e = others.map.current.get(id); if (!e) continue;
      const x = e.target.x, z = e.target.z;
      prey.push({ id, eyes: !!e.eyes, x, z, out: Math.hypot(x, z) > WORLD.YARD_R && now > (grace.current[id] || 0) });
    }
    // THE UNSTABLE EYES: a brewer wearing them is hunted by a SECOND guard too - the nearest other zone's (by home), chosen once per frame
    for (const q of prey) {
      q.aux = -1;
      if (!q.eyes || !q.out) continue;
      const z0 = zoneOf(q.x, q.z); let bd = 1e9;
      st.forEach((b, i) => { if (b.zone === z0) return; const d = Math.hypot(q.x - b.hx, q.z - b.hz); if (d < bd) { bd = d; q.aux = i; } });
    }
    // HEAT: seconds each brewer has spent outside the lanterns. It ramps the beast's speed; the yard cools it down.
    for (const q of prey) {
      const k = q.id === null ? "me" : q.id, h = heat.current[k] || 0;
      heat.current[k] = q.out ? h + dt : Math.max(0, h - BEAST.COOL * dt);
    }
    let caught = false;
    st.forEach((b, i) => {
      let tgt = null, best = aggro;
      for (const q of prey) { if (!q.out || (zoneOf(q.x, q.z) !== b.zone && q.aux !== i)) continue; const d = Math.hypot(q.x - b.x, q.z - b.z); if (d < best) { best = d; tgt = q; } }
      let tx, tz, v;
      if (tgt) { tx = tgt.x; tz = tgt.z; v = beastSpeed(heat.current[tgt.id === null ? "me" : tgt.id] || 0, night, charmK(WILD.charm, now) * (tgt.eyes ? BEAST.EYES_K : 1) * bloodK); b.chasing = true; }
      else {
        b.chasing = false; b.wt -= dt;
        if (b.wt <= 0) { const a = rng.current() * 6.283, r = rng.current() * 5; b.wx = b.hx + Math.cos(a) * r; b.wz = b.hz + Math.sin(a) * r; b.wt = 3 + rng.current() * 4; }
        tx = b.wx; tz = b.wz; v = BEAST.WANDER;
        if (Math.hypot(b.x - b.hx, b.z - b.hz) > 7) { tx = b.hx; tz = b.hz; v = BEAST.RETURN; }   // you got away: it trots home
      }
      const dx = tx - b.x, dz = tz - b.z, dd = Math.hypot(dx, dz);
      if (dd > 0.15) {
        let nx = b.x + (dx / dd) * v * dt, nz = b.z + (dz / dd) * v * dt;
        const rr = Math.hypot(nx, nz), wall = WORLD.YARD_R + 0.7;    // THE LANTERN LINE: they will not cross it
        if (rr < wall) { nx *= wall / rr; nz *= wall / rr; }
        b.x = nx; b.z = nz; b.yaw = Math.atan2(dx, dz);
      }
      const g = refs.current[i];
      if (g) { g.position.set(b.x, 0, b.z); g.rotation.y = b.yaw; }
      if (tgt && best < BEAST.CATCH) {
        tgt.out = false;                                // one catch per brewer per frame (two beasts in reach = one catch)
        if (tgt.id === null) caught = true;
        else {                                          // a friend: tell them; this beast goes home; they get their safe seconds
          sendCatch(sh.sid, tgt.id); heat.current[tgt.id] = 0;
          grace.current[tgt.id] = now + BEAST.GRACE_MS + 1500;
          b.x = b.hx; b.z = b.hz; b.chasing = false;
        }
      }
    });
    if (caught) {
      heat.current.me = 0;
      st.forEach((b) => { b.x = b.hx; b.z = b.hz; b.chasing = false; });
      catchMe();
    }
    if (keeping && now - lastSend.current > (st.some((b) => b.chasing) ? BEAST_SEND_CHASE_MS : BEAST_SEND_IDLE_MS)) {
      lastSend.current = now; sendBeasts(sh.sid, sh.uid, st);
    }
  });
  useEffect(() => { window.__moonBeasts = () => st.map((b) => ({ x: +b.x.toFixed(1), z: +b.z.toFixed(1), chasing: b.chasing })); }, [st]);
  return <group>{GEN.beasts.map((_, i) => <PhantomBeast key={i} refCb={(el) => { refs.current[i] = el; }} />)}</group>;
}

// ── STARS + REVENUERS (2026-10-31) ───────────────────────────
// LOSING THEM: the whole crew (me + every other brewer on the server - the same feed the beasts hunt) has to stay out past the
// lanterns in ONE go for clearMs(game). Refs only (PERF LAW): WILD.clear is what the HUD's "losing them" bar reads.
// Only the solo player or the keeper sends the clear (a guest's screen just measures, for its own bar). One send per heat.
function ClearWatch({ posRef, gameRef, others, modeRef, cb }) {
  const sent = useRef(0);                               // the star count a clear was already sent for (0 = none yet)
  useFrame(() => {
    const g = gameRef.current, c = WILD.clear, stars = (g && g.stars) || 0;
    if (!stars) { c.since = 0; c.need = 0; sent.current = 0; return; }
    if (sent.current && sent.current !== stars) sent.current = 0;           // the heat changed: a fresh clear can go
    const p = posRef.current, now = Date.now();
    let allOut = Math.hypot(p.x, p.z) >= WORLD.YARD_R;
    if (allOut && others) for (const id of others.ids) {
      const e = others.map.current.get(id);
      if (e && Math.hypot(e.target.x, e.target.z) < WORLD.YARD_R) { allOut = false; break; }
    }
    c.need = clearMs(g);
    if (!allOut) { c.since = 0; return; }
    if (!c.since) c.since = now;
    if (now - c.since >= c.need && !sent.current && modeRef.current !== "guest" && cb.onClearStars) { sent.current = stars; cb.onClearStars(); }
  });
  return null;
}
// THE FUZZ IS COMING: red / blue flashing out past the lanterns on the yard's south edge, the last HEAT.WARN_S seconds.
// PLACEHOLDER look. No new lights (the light-budget law): two unlit blobs + additive glow sprites. One useFrame.
const glowTexture = () => {
  const c = document.createElement("canvas"); c.width = c.height = 64;
  const x = c.getContext("2d"), gr = x.createRadialGradient(32, 32, 0, 32, 32, 32);
  gr.addColorStop(0, "rgba(255,255,255,1)"); gr.addColorStop(0.3, "rgba(255,255,255,.5)"); gr.addColorStop(1, "rgba(255,255,255,0)");
  x.fillStyle = gr; x.fillRect(0, 0, 64, 64);
  return new THREE.CanvasTexture(c);
};
function FuzzLights({ gameRef }) {
  const root = useRef(), glow = useRef([]);
  const tex = useMemo(glowTexture, []);
  useEffect(() => () => tex.dispose(), [tex]);
  useFrame(({ clock }) => {
    const g = gameRef.current, left = g && g.stars > 0 ? fuzzIn(g, Date.now()) : null, on = left != null && left < HEAT.WARN_S * 1000;
    if (!root.current) return;
    root.current.visible = on;
    if (!on) return;
    const t = clock.elapsedTime, red = Math.floor(t * 3) % 2 === 0, k = 0.8 + 0.2 * Math.sin(t * 20);
    glow.current.forEach((sp, i) => {
      if (!sp) return;
      const lit = (i === 0) === red;
      sp.scale.setScalar(lit ? 3.4 * k : 0.7);
      sp.material.opacity = lit ? 0.95 : 0.2;
    });
  });
  const LIGHTS = [[-0.7, "#ff2a2a"], [0.7, "#2a5aff"]];
  return (
    <group ref={root} position={[0, 0, WORLD.YARD_R + 1.6]} visible={false}>
      {LIGHTS.map(([x, col], i) => (
        <group key={i} position={[x, 1.5, 0]}>
          <mesh><sphereGeometry args={[0.16, 10, 8]} /><meshBasicMaterial color={col} toneMapped={false} fog={false} /></mesh>
          <sprite ref={(el) => { glow.current[i] = el; }} scale={[2, 2, 1]}>
            <spriteMaterial map={tex} color={col} transparent depthWrite={false} blending={THREE.AdditiveBlending} fog={false} toneMapped={false} />
          </sprite>
        </group>
      ))}
    </group>
  );
}

// ── THE BLOOD MOON's rain (2026-10-31): ~600 light red drops falling round the camera, recycled (wrapped) as you move.
// One THREE.Points, one useFrame, no lights; hidden (and skipped) when it isn't a blood night. PLACEHOLDER look.
const RAIN = { N: 600, BOX: 14, TOP: 12 };
function BloodRain() {
  const pts = useRef(), mat = useRef(), k = useRef(0);
  const { camera } = useThree();
  const [pos, speed] = useMemo(() => {
    const r = mulberry32(77), a = new Float32Array(RAIN.N * 3), v = new Float32Array(RAIN.N);
    for (let i = 0; i < RAIN.N; i++) { a[i * 3] = (r() - 0.5) * 2 * RAIN.BOX; a[i * 3 + 1] = r() * RAIN.TOP; a[i * 3 + 2] = (r() - 0.5) * 2 * RAIN.BOX; v[i] = 7 + r() * 4; }
    return [a, v];
  }, []);
  useFrame((_, dtRaw) => {
    const p = pts.current; if (!p) return;
    const dt = Math.min(dtRaw, 0.05);
    k.current += ((isBloodMoon(Date.now()) ? 1 : 0) - k.current) * Math.min(1, dt * 0.6);
    p.visible = k.current > 0.02;
    if (!p.visible) return;
    if (mat.current) mat.current.opacity = 0.55 * k.current;
    const a = p.geometry.attributes.position.array, cx = camera.position.x, cz = camera.position.z, B = RAIN.BOX;
    for (let i = 0; i < RAIN.N; i++) {
      const j = i * 3;
      a[j + 1] -= speed[i] * dt; if (a[j + 1] < 0) a[j + 1] += RAIN.TOP;
      if (a[j] - cx > B) a[j] -= 2 * B; else if (a[j] - cx < -B) a[j] += 2 * B;
      if (a[j + 2] - cz > B) a[j + 2] -= 2 * B; else if (a[j + 2] - cz < -B) a[j + 2] += 2 * B;
    }
    p.geometry.attributes.position.needsUpdate = true;
  });
  return (
    <points ref={pts} frustumCulled={false} visible={false}>
      <bufferGeometry><bufferAttribute attach="attributes-position" args={[pos, 3]} /></bufferGeometry>
      <pointsMaterial ref={mat} color={BLOOD.RAIN} size={0.07} sizeAttenuation transparent opacity={0} depthWrite={false} />
    </points>
  );
}

// ── THE TROPHY SHELF + THE AGING RACK (2026-10-31) — PLACEHOLDER primitives until the Blender pass ──
// They stand on the yard's west side by the shack, facing the yard centre. The spots were measured against GEN.solids (worldgen):
// the shelf ~1.1 m and the rack ~2.2 m clear of every collider (the tub, the sacks, the shack), out of the door's line and the work lanes.
// Walk-through (no collider added - the rules' colliders stay worldgen's). No lights; memo'd, so it rebuilds only when a list changes.
const SHELF_AT = [-5.75, -0.35], RACK_AT = [-5.9, 0.85];
const TROPHY_TINT = { legendary: "#ff8ad8", prime: "#f2c94c" };
const WOOD = <meshStandardMaterial color="#6e4a2c" roughness={1} />;
const facing = ([x, z]) => Math.atan2(-x, -z);           // local +z faces the yard centre
function GlassJar({ x, y, tint, label }) {
  return (
    <group position={[x, y, 0]}>
      <mesh position={[0, 0.12, 0]}><cylinderGeometry args={[0.085, 0.09, 0.24, 12]} /><meshStandardMaterial color={tint} emissive={tint} emissiveIntensity={0.25} transparent opacity={0.72} roughness={0.15} metalness={0.1} /></mesh>
      <mesh position={[0, 0.255, 0]}><cylinderGeometry args={[0.06, 0.06, 0.03, 10]} /><meshStandardMaterial color="#8a8f96" metalness={0.6} roughness={0.4} /></mesh>
      {label ? <mesh position={[0, 0.12, 0.092]}><planeGeometry args={[0.14, 0.105]} /><meshBasicMaterial map={label} toneMapped={false} /></mesh> : null}
    </group>
  );
}
const YardShelves = memo(function YardShelves({ trophies, shelf, brand }) {
  const art = brand && brand.art, mine = brand && brand.name;
  // my label as a texture: built once per art change, freed when it changes
  const label = useMemo(() => {
    if (!art || typeof document === "undefined") return null;
    const c = document.createElement("canvas"), S = 4; c.width = LABEL_W * S; c.height = LABEL_H * S;
    drawLabel(c.getContext("2d"), art, LABEL_W, LABEL_H, S);
    const tx = new THREE.CanvasTexture(c); tx.colorSpace = THREE.SRGBColorSpace; tx.magFilter = tx.minFilter = THREE.NearestFilter; return tx;
  }, [art]);
  useEffect(() => () => { if (label) label.dispose(); }, [label]);
  const tro = (trophies || []).slice(0, TROPHY.SLOTS), rack = (shelf || []).slice(0, AGE.SLOTS);
  const per = Math.ceil(TROPHY.SLOTS / 2), sx = (i, n, w) => (n > 1 ? -w / 2 + (w * i) / (n - 1) : 0);
  return (
    <group>
      <group position={[SHELF_AT[0], 0, SHELF_AT[1]]} rotation={[0, facing(SHELF_AT), 0]}>
        {[-0.78, 0.78].map((x) => <mesh key={x} position={[x, 0.75, 0]}><boxGeometry args={[0.07, 1.5, 0.32]} />{WOOD}</mesh>)}
        {[0.12, 0.62, 1.12].map((y) => <mesh key={y} position={[0, y, 0]}><boxGeometry args={[1.62, 0.05, 0.34]} />{WOOD}</mesh>)}
        <mesh position={[0, 0.75, -0.16]}><boxGeometry args={[1.62, 1.5, 0.03]} /><meshStandardMaterial color="#4a3020" roughness={1} /></mesh>
        {tro.map((j, i) => <GlassJar key={i} x={sx(i % per, per, 1.2)} y={i < per ? 1.145 : 0.645} tint={TROPHY_TINT[j.q] || "#8fd88a"} label={label && j.lb && j.lb === mine ? label : null} />)}
      </group>
      <group position={[RACK_AT[0], 0, RACK_AT[1]]} rotation={[0, facing(RACK_AT), 0]}>
        {[-0.5, 0.5].map((x) => <mesh key={x} position={[x, 0.42, 0]}><boxGeometry args={[0.06, 0.84, 0.28]} />{WOOD}</mesh>)}
        {[0.1, 0.55].map((y) => <mesh key={y} position={[0, y, 0]}><boxGeometry args={[1.06, 0.045, 0.3]} />{WOOD}</mesh>)}
        {rack.map((x, i) => <GlassJar key={i} x={sx(i, AGE.SLOTS, 0.72)} y={0.572} tint="#c8862a" label={label && x.jar && x.jar.lb && x.jar.lb === mine ? label : null} />)}
      </group>
    </group>
  );
});

// ── THE YARD DECORATIONS (2026-10-31, ROADMAP 40) — PLACEHOLDER primitives until the art pass ──
// One per owned DECOR id, standing at DECOR[id].at = [x, z, ry] (the yard's centre never moves, so the spots hold at every camp size).
// Walk-through (no colliders), NO lights (the bulbs are unlit emissive-looking basic material), one useFrame for the little motions.
// The flag carries caP'n's IE heart (red spray-painted heart, crossed arrows, X'd-out eyes) drawn in code onto a CanvasTexture - no file, no drain.
function heartFlagTexture() {
  const W = 256, H = 176, c = document.createElement("canvas"); c.width = W; c.height = H;
  const x = c.getContext("2d"), r = mulberry32(31337);
  x.fillStyle = "#1d1619"; x.fillRect(0, 0, W, H);                                      // weathered black cloth
  for (let i = 0; i < 900; i++) { x.fillStyle = `rgba(255,240,220,${0.02 + r() * 0.04})`; x.fillRect(r() * W, r() * H, 1 + r() * 2, 1); }
  const cx = W / 2, cy = H / 2 + 4;
  // the crossed arrows, behind the heart
  const arrow = (x0, y0, x1, y1) => {
    const a = Math.atan2(y1 - y0, x1 - x0);
    x.strokeStyle = "#e9dcc2"; x.lineWidth = 5; x.lineCap = "round";
    x.beginPath(); x.moveTo(x0, y0); x.lineTo(x1, y1); x.stroke();
    x.fillStyle = "#e9dcc2"; x.beginPath();                                             // the head
    x.moveTo(x1 + Math.cos(a) * 14, y1 + Math.sin(a) * 14);
    x.lineTo(x1 + Math.cos(a + 2.4) * 13, y1 + Math.sin(a + 2.4) * 13);
    x.lineTo(x1 + Math.cos(a - 2.4) * 13, y1 + Math.sin(a - 2.4) * 13); x.closePath(); x.fill();
    x.lineWidth = 3;                                                                     // the fletching
    for (const d of [4, 12]) for (const s of [1, -1]) {
      const bx = x0 + Math.cos(a) * d, by = y0 + Math.sin(a) * d;
      x.beginPath(); x.moveTo(bx, by); x.lineTo(bx + Math.cos(a + s * 2.5) * 11, by + Math.sin(a + s * 2.5) * 11); x.stroke();
    }
  };
  arrow(cx - 92, cy + 62, cx + 84, cy - 58); arrow(cx + 92, cy + 62, cx - 84, cy - 58);
  // the heart, sprayed: overspray speckle first, then the body, then a couple of drips
  const heart = () => { x.beginPath(); x.moveTo(cx, cy + 46);
    x.bezierCurveTo(cx - 64, cy + 4, cx - 54, cy - 52, cx, cy - 22);
    x.bezierCurveTo(cx + 54, cy - 52, cx + 64, cy + 4, cx, cy + 46); x.closePath(); };
  for (let i = 0; i < 700; i++) { const a = r() * 6.283, d = 40 + r() * 34; x.fillStyle = `rgba(214,30,42,${0.15 + r() * 0.5})`; x.beginPath(); x.arc(cx + Math.cos(a) * d * 1.05, cy - 2 + Math.sin(a) * d * 0.85, 0.6 + r() * 1.6, 0, 6.283); x.fill(); }
  heart(); x.fillStyle = "#d61e2a"; x.fill();
  heart(); x.save(); x.clip(); x.fillStyle = "rgba(255,120,110,.35)"; x.beginPath(); x.ellipse(cx - 22, cy - 14, 16, 10, -0.5, 0, 6.283); x.fill(); x.restore();
  x.fillStyle = "#d61e2a"; for (const [dx, len] of [[-18, 26], [9, 38], [24, 18]]) { x.fillRect(cx + dx - 2, cy + 30 - Math.abs(dx) * 0.5, 4, len); x.beginPath(); x.arc(cx + dx, cy + 30 - Math.abs(dx) * 0.5 + len, 3.2, 0, 6.283); x.fill(); }
  // the X'd-out eyes
  x.strokeStyle = "#16090b"; x.lineWidth = 6; x.lineCap = "round";
  for (const ex of [cx - 20, cx + 20]) { const ey = cy - 6, k = 9; x.beginPath(); x.moveTo(ex - k, ey - k); x.lineTo(ex + k, ey + k); x.moveTo(ex + k, ey - k); x.lineTo(ex - k, ey + k); x.stroke(); }
  const t = new THREE.CanvasTexture(c); t.colorSpace = THREE.SRGBColorSpace; return t;
}
function HeartFlag({ swayRef }) {
  const tex = useMemo(heartFlagTexture, []);
  useEffect(() => () => tex.dispose(), [tex]);
  return (
    <group>
      <mesh position={[0, 1.5, 0]}><cylinderGeometry args={[0.04, 0.05, 3.0, 6]} /><meshStandardMaterial color="#3a2a1c" roughness={1} /></mesh>
      <mesh position={[0, 3.03, 0]}><sphereGeometry args={[0.06, 8, 6]} /><meshStandardMaterial color="#b8693a" metalness={0.6} roughness={0.4} /></mesh>
      <group ref={swayRef} position={[0.04, 2.55, 0]}>
        <mesh position={[0.6, 0, 0]}><planeGeometry args={[1.2, 0.82]} /><meshStandardMaterial map={tex} side={THREE.DoubleSide} roughness={1} /></mesh>
      </group>
    </group>
  );
}
const BULB_COLS = ["#ffd27a", "#ff9a5a", "#f6e3a0", "#ff7a6a", "#ffe9b0"];
function StringLights() {
  const SPAN = 2.1, TOP = 2.35, SAG = 0.45, N = 11;            // poles at +-SPAN m along local x (clear of the tub, the jug and the crate)
  const yAt = (x) => TOP - SAG * (1 - (x / SPAN) ** 2);
  const curve = useMemo(() => new THREE.CatmullRomCurve3(Array.from({ length: 9 }, (_, i) => { const x = -SPAN + (2 * SPAN * i) / 8; return new THREE.Vector3(x, yAt(x), 0); })), []);   // eslint-disable-line react-hooks/exhaustive-deps -- fixed dials
  return (
    <group>
      {[-SPAN, SPAN].map((x) => <mesh key={x} position={[x, TOP / 2 + 0.05, 0]}><cylinderGeometry args={[0.045, 0.055, TOP + 0.1, 6]} /><meshStandardMaterial color="#3a2a1c" roughness={1} /></mesh>)}
      <mesh><tubeGeometry args={[curve, 24, 0.012, 4, false]} /><meshBasicMaterial color="#1a1410" /></mesh>
      {Array.from({ length: N }, (_, i) => { const x = -SPAN + (2 * SPAN * (i + 0.5)) / N; return (
        <mesh key={i} position={[x, yAt(x) - 0.07, 0]}><sphereGeometry args={[0.055, 8, 6]} /><meshBasicMaterial color={BULB_COLS[i % BULB_COLS.length]} toneMapped={false} /></mesh>
      ); })}
    </group>
  );
}
function RockingChair({ rockRef }) {
  const W = <meshStandardMaterial color="#5c3d24" roughness={1} />;
  return (
    <group ref={rockRef}>
      {[-0.26, 0.26].map((x) => <group key={x}>
        <group position={[x, 0.93, -0.04]} rotation={[0, Math.PI / 2, 0]}>{/* a rocker: a 0.9 rad arc of a 0.9 m ring, its middle on the ground */}
          <mesh rotation={[0, 0, -Math.PI / 2 - 0.45]}><torusGeometry args={[0.9, 0.025, 4, 16, 0.9]} />{W}</mesh>
        </group>
        <mesh position={[x, 0.27, 0.12]}><boxGeometry args={[0.04, 0.4, 0.04]} />{W}</mesh>
        <mesh position={[x, 0.27, -0.2]}><boxGeometry args={[0.04, 0.4, 0.04]} />{W}</mesh>
        <mesh position={[x, 0.66, -0.02]}><boxGeometry args={[0.04, 0.04, 0.42]} />{W}</mesh>
      </group>)}
      <mesh position={[0, 0.48, -0.03]}><boxGeometry args={[0.56, 0.05, 0.46]} />{W}</mesh>
      {[-0.18, -0.06, 0.06, 0.18].map((x) => <mesh key={x} position={[x, 0.86, -0.26]} rotation={[-0.18, 0, 0]}><boxGeometry args={[0.07, 0.72, 0.03]} />{W}</mesh>)}
      <mesh position={[0, 1.22, -0.33]} rotation={[-0.18, 0, 0]}><boxGeometry args={[0.6, 0.06, 0.05]} />{W}</mesh>
    </group>
  );
}
function Scarecrow() {
  const STRAW = <meshStandardMaterial color="#d9b25a" roughness={1} />;
  return (
    <group>
      <mesh position={[0, 0.95, 0]}><cylinderGeometry args={[0.05, 0.06, 1.9, 6]} /><meshStandardMaterial color="#4a3422" roughness={1} /></mesh>
      <mesh position={[0, 1.45, 0]} rotation={[0, 0, Math.PI / 2]}><cylinderGeometry args={[0.035, 0.035, 1.5, 6]} /><meshStandardMaterial color="#4a3422" roughness={1} /></mesh>
      <mesh position={[0, 1.3, 0]}><boxGeometry args={[0.55, 0.62, 0.26]} /><meshStandardMaterial color="#7a3a2a" roughness={1} /></mesh>
      <mesh position={[0, 1.45, 0]}><boxGeometry args={[1.25, 0.16, 0.18]} /><meshStandardMaterial color="#7a3a2a" roughness={1} /></mesh>
      {[-0.68, 0.68].map((x) => <mesh key={x} position={[x, 1.45, 0]} rotation={[0, 0, x > 0 ? -Math.PI / 2 : Math.PI / 2]}><coneGeometry args={[0.09, 0.18, 6]} />{STRAW}</mesh>)}
      <mesh position={[0, 0.94, 0]} rotation={[Math.PI, 0, 0]}><coneGeometry args={[0.2, 0.24, 7]} />{STRAW}</mesh>
      <mesh position={[0, 1.83, 0]}><sphereGeometry args={[0.2, 12, 10]} /><meshStandardMaterial color="#c9a46a" roughness={1} /></mesh>
      {[-0.07, 0.07].map((x) => <mesh key={x} position={[x, 1.86, 0.18]}><sphereGeometry args={[0.025, 6, 6]} /><meshBasicMaterial color="#1a1010" /></mesh>)}
      <mesh position={[0, 1.99, 0]}><cylinderGeometry args={[0.34, 0.34, 0.025, 14]} /><meshStandardMaterial color="#5a4630" roughness={1} /></mesh>
      <mesh position={[0, 2.12, 0]}><coneGeometry args={[0.17, 0.3, 10]} /><meshStandardMaterial color="#5a4630" roughness={1} /></mesh>
    </group>
  );
}
function WindChimes({ swingRef }) {
  const TIN = <meshStandardMaterial color="#c8ccd2" metalness={0.75} roughness={0.3} />;
  return (
    <group>
      <mesh position={[0, 1.1, 0]}><cylinderGeometry args={[0.045, 0.055, 2.2, 6]} /><meshStandardMaterial color="#3a2a1c" roughness={1} /></mesh>
      <mesh position={[0.3, 2.15, 0]} rotation={[0, 0, Math.PI / 2]}><cylinderGeometry args={[0.025, 0.025, 0.62, 6]} /><meshStandardMaterial color="#3a2a1c" roughness={1} /></mesh>
      <group ref={swingRef} position={[0.55, 2.13, 0]}>
        <mesh position={[0, -0.12, 0]}><cylinderGeometry args={[0.12, 0.12, 0.02, 12]} /><meshStandardMaterial color="#6e4a2c" roughness={1} /></mesh>
        {[0.34, 0.42, 0.5, 0.38, 0.46].map((len, i) => { const a = (i / 5) * Math.PI * 2; return (
          <mesh key={i} position={[Math.cos(a) * 0.09, -0.16 - len / 2, Math.sin(a) * 0.09]}><cylinderGeometry args={[0.012, 0.012, len, 6]} />{TIN}</mesh>
        ); })}
      </group>
    </group>
  );
}
const YardDecor = memo(function YardDecor({ ids }) {
  const rock = useRef(), sway = useRef(), swing = useRef();
  useFrame(({ clock }) => {
    const t = clock.elapsedTime;
    if (rock.current) rock.current.rotation.x = Math.sin(t * 1.3) * 0.07;
    if (sway.current) sway.current.rotation.y = Math.sin(t * 0.9) * 0.22 + Math.sin(t * 2.3) * 0.05;
    if (swing.current) { swing.current.rotation.z = Math.sin(t * 1.7) * 0.08; swing.current.rotation.x = Math.sin(t * 1.1 + 1) * 0.06; }
  });
  const list = ids ? ids.split(",").filter((id) => DECOR[id]) : [];
  return (
    <group>
      {list.map((id) => { const [x, z, ry] = DECOR[id].at; return (
        <group key={id} position={[x, 0, z]} rotation={[0, ry || 0, 0]}>
          {id === "lights" ? <StringLights /> : id === "chair" ? <RockingChair rockRef={rock} /> : id === "heart" ? <HeartFlag swayRef={sway} />
            : id === "scarecrow" ? <Scarecrow /> : id === "chimes" ? <WindChimes swingRef={swing} /> : null}
        </group>
      ); })}
    </group>
  );
});

const GatherSpotsOrNodes = ({ kit }) => (kit ? <GatherSpots kit={kit} WILD={WILD} /> : <ForageNodes />);

// QA: the live three scene for the test harness (read-only peeking; nothing in the game uses it)
function QaScene() { const { scene } = useThree(); useEffect(() => { window.__moonScene = scene; }, [scene]); return null; }

// ── THE SCENE ────────────────────────────────────────────────
export default function World3D({ gender, frozen, bagCount, fx, fx2 = "idle", spookedUntil, game, cb, online }) {
  // THE LIVE CAMP (2026-10-31, ROADMAP 40): the world is built for the camp of the yard I'm STANDING IN (the view - a guest gets the
  // keeper's camp). setCampLevel is keyed (same camp = nothing rebuilt) and rewrites GEN / DRESS / WORLD / PLACES before anything
  // below reads them; everything that baked them (instances, memos) sits under <group key={campKey()}> and remounts when it changes.
  setCampLevel(game ? game.camp : 0, { shack: game ? game.shack : 0, still2: !!(game && game.stills === 2) });
  const world = campKey();
  const posRef = useRef(new THREE.Vector3(WORLD.SPAWN[0], 0, WORLD.SPAWN[1]));
  const yawRef = useRef(Math.PI);                       // spawn facing the still
  const speedRef = useRef(0);
  // CO-OP step 1 (2026-10-24): the other brewers on your server (moonshineBrewers.js). Signed out: sid null, nothing.
  const sid = online && online.sid, meUid = online && online.uid;
  const frozenRef = useRef(false); frozenRef.current = !!frozen;
  const eyesRef = useRef(false); eyesRef.current = !!(game && game.specs && game.eyes);
  WILD.eyes = eyesRef.current;
  useBrewerSync({ sid, uid: meUid, name: online && online.name, posRef, yawRef, speedRef, gender, eyesRef });
  const others = useRemoteBrewers(sid, meUid);
  WILD.sid = sid || null;
  WILD.myName = (online && online.name) || null;
  useSharedPicks(sid, WILD.pickedAt);
  useSharedWisp(sid, WILD);                             // ...and one Stray Wisp for the whole crew (2026-10-30)                    // CO-OP step 3: a spot one of us picked is gone for all of us
  const share = sid ? { sid, uid: meUid, mode: (online && online.mode) || "solo", keeperUid: online && online.keeperUid } : null;
  const modeRef = useRef("solo"); modeRef.current = (online && online.mode) || "solo";   // STARS: only solo / the keeper sends the clear
  const keysRef = useRef({});
  const actRef = useRef(false);
  const bagRef = useRef(bagCount);
  bagRef.current = bagCount;
  const spookRef = useRef(spookedUntil || 0);
  spookRef.current = spookedUntil || 0;
  const gameRef = useRef(game);
  gameRef.current = game;                               // the frame loops read the live game state through this (never React in a frame)
  WILD.charm = game && game.charm ? game.charm : null;   // a lit charm slows the guards (the keeper's sim reads the yard's charm)
  WILD.fx.still = fx;                                   // the still glows/steams/smokes from this
  WILD.fx.still2 = fx2;                                 // ...and THE SECOND STILL from this
  const kit = useKit();                                 // null until moonshine_kit.glb lands -> placeholders meanwhile
  const seized = !!(game && game.seized), still2 = !!(game && game.stills === 2);   // the revenuers took the still(s) / THE SECOND STILL stands
  const shackS = (YARD_UP.shack[game ? game.shack : 0] || YARD_UP.shack[0]).s;
  const decorIds = game && game.decor ? Object.keys(DECOR).filter((id) => game.decor[id]).join(",") : "";

  useEffect(() => {
    const dn = (e) => {
      if (typing(e)) return;                                          // a name / password / friend code (or an online card is up)
      const k = e.key.length === 1 ? e.key.toLowerCase() : e.key;     // "W" and "w" are the SAME key (Shift mid-hold)
      keysRef.current[k] = true;
      if (k === "Shift") keysRef.current.shift = true;
      const onBody = document.activeElement === document.body || !document.activeElement;
      // one action per press: no auto-repeat vacuum; Enter only when no HUD button has focus
      if (!e.repeat && k === "v" && cb.onEyes && !frozenRef.current) cb.onEyes();
      if (!e.repeat && (k === "e" || (k === "Enter" && onBody))) actRef.current = true;
      if (k.startsWith("Arrow") || (k === " " && onBody)) e.preventDefault();
    };
    const up = (e) => {
      const k = e.key.length === 1 ? e.key.toLowerCase() : e.key;
      keysRef.current[k] = false;
      if (k === "Shift") keysRef.current.shift = false;
    };
    const blur = () => { keysRef.current = {}; };
    window.addEventListener("keydown", dn); window.addEventListener("keyup", up); window.addEventListener("blur", blur);
    return () => { window.removeEventListener("keydown", dn); window.removeEventListener("keyup", up); window.removeEventListener("blur", blur); };
  }, []);

  useEffect(() => {          // dev / QA handles
    window.__moon = {
      pos: () => ({ x: +posRef.current.x.toFixed(2), z: +posRef.current.z.toFixed(2) }),
      teleport: (x, z) => { posRef.current.set(x, 0, z); WILD.snapCam = true; },     // the camera comes too (QA)
      get nodes() { return GEN.nodes; }, places: PLACES, camp: () => campKey(), beasts: () => (window.__moonBeasts ? window.__moonBeasts() : []),
      act: () => { actRef.current = true; }, key: (k, v) => { keysRef.current[k] = v; },
      invuln: (ms) => { WILD.invulnUntil = Date.now() + ms; },
      picked: () => ({ ...WILD.pickedAt }),
    };
  }, []);

  return (
    <Canvas dpr={[1, 1.75]} camera={{ fov: 52, near: 0.1, far: 400, position: [0, 6, 14] }} gl={{ antialias: true }}>
      <Sky />
      <FrameWatch />
      <QaScene />
      <group key={world}>{/* THE LIVE CAMP: everything built from GEN / DRESS / WORLD / PLACES remounts when the camp changes */}
        <Ground />
        {kit ? <Woods kit={kit} posRef={posRef} /> : <><Trees /><RuinStones /></>}
        <Yard kit={kit} seized={seized} still2={still2} shackS={shackS} />
        {kit ? <StillRig kit={kit} rig={game && game.rig} seized={seized} /> : null}
        {kit && still2 ? <StillRig kit={kit} rig={game && game.rig2} seized={seized} at={LAYOUT.still2} /> : null}
        {!hasRigKit(kit) && !seized ? <RigParts rig={game && game.rig} /> : null}
        {!hasRigKit(kit) && !seized && still2 ? <RigParts rig={game && game.rig2} k={1} /> : null}
        <GatherSpotsOrNodes kit={kit} />
        <UnstableSights gameRef={gameRef} />
        <Beasts posRef={posRef} bagRef={bagRef} cb={cb} share={share} others={others} />
        <FuzzLights gameRef={gameRef} />
      </group>
      <StationFx gameRef={gameRef} />
      <TutBeacon gameRef={gameRef} posRef={posRef} />
      <Townsfolk game={game} />
      <ClearWatch posRef={posRef} gameRef={gameRef} others={others} modeRef={modeRef} cb={cb} />
      <BloodRain />
      <YardShelves trophies={game && game.trophies} shelf={game && game.shelf} brand={game && game.brand} />
      <YardDecor ids={decorIds} />
      <Hero gender={gender} posRef={posRef} yawRef={yawRef} speedRef={speedRef} />
      <RemoteBrewers store={others} />
      <Controller posRef={posRef} yawRef={yawRef} speedRef={speedRef} keysRef={keysRef} actRef={actRef} frozen={frozen} bagRef={bagRef} spookRef={spookRef} gameRef={gameRef} cb={cb} />
      <CameraRig posRef={posRef} yawRef={yawRef} />
    </Canvas>
  );
}
